import { z } from 'zod';
import { normalTitle } from './compliance-fixes.js';
import { CIS_LEVEL_BY_TITLE } from './cis-levels.data.js';
/**
 * CIS Level 1 and Level 2 (P11.21). A benchmark's recommendations belong to
 * profiles — "Level 1 - Server" is the baseline an ordinary server is held to,
 * "Level 2 - Server" adds the stricter ones that cost something to run (separate
 * partitions for /var/log/audit, auditd rules) — and a scanner that scores every
 * recommendation as if all were required makes every server look worse than it is.
 * Tenable and OpenSCAP scan against a profile for that reason.
 *
 * **Wazuh's policy files carry no level**, and the benchmarks are not Bartizo's to
 * ship, so the level comes from ComplianceAsCode's open CIS control files, matched
 * by the recommendation's title (CIS keeps titles across Ubuntu releases, as the fix
 * catalogue relies on). Measured against Wazuh 4.14.8's own policies: every check of
 * the 22.04 policy has a level, 278 of 279 of 24.04's, and 131 of 210 of 20.04's,
 * whose wording differs from 22.04's more. **A check whose level is not known stays
 * in scope** and is labelled so: leaving it out would quietly shrink the score.
 */
/** What a page can be scoped to. `l2` is Level 2 *including* Level 1, as the profile is. */
export const CisLevelScope = z.enum(['all', 'l1', 'l2']);
export const CIS_LEVEL_SCOPE_LABEL = {
    all: 'All checks',
    l1: 'Level 1 server',
    l2: 'Level 2 server (includes Level 1)',
};
/** The level of a check by its title, or null where no source of levels knows it. */
export function cisLevelOf(title) {
    const key = normalTitle(title).replace(/\s*\((automated|manual)\)$/, '');
    return Object.hasOwn(CIS_LEVEL_BY_TITLE, key) ? (CIS_LEVEL_BY_TITLE[key] ?? null) : null;
}
/**
 * Whether a check of this level is in a scope. A level not known (null) is in both
 * server scopes; a workstation-only recommendation is in neither.
 */
export function inLevelScope(level, scope) {
    if (scope === 'all')
        return true;
    if (level === null)
        return true;
    return scope === 'l1' ? level === 1 : level === 1 || level === 2;
}
/**
 * How `sca_checks.cis_level` stores it: the level itself, `-1` where it is not
 * known (or the policy is not a CIS one), `-2` before it has been worked out.
 */
export const CIS_LEVEL_UNKNOWN = -1;
export const CIS_LEVEL_NOT_COMPUTED = -2;
//# sourceMappingURL=cis-levels.js.map